/**
 * FileUploadZone Component (AC-808)
 *
 * A drag-and-drop file upload zone with file type and size validation.
 * Supports click-to-select via hidden file input.
 *
 * Technical Design Reference: docs/technical/TDD-AC-804-knowledge-base-file-upload.md
 */

import React, { useState, useRef, useCallback } from 'react';
import { Upload, FileText, AlertCircle } from 'lucide-react';

interface FileUploadZoneProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
  maxFiles?: number;
  maxSizeMB?: number;
  acceptedFormats?: string[];
}

interface ValidationError {
  type: 'format' | 'size' | 'batch';
  message: string;
  details?: string;
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  onFilesSelected,
  disabled = false,
  maxFiles = 10,
  maxSizeMB = 10,
  acceptedFormats = ['.pdf', '.txt', '.md', '.docx'],
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<ValidationError | null>(null);
  const [skippedFiles, setSkippedFiles] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatDisplayList = acceptedFormats.map(f => f.replace('.', '').toUpperCase()).join(', ');
  const formatAcceptString = acceptedFormats.join(',');

  const getFileExtension = (filename: string): string => {
    const lastDot = filename.lastIndexOf('.');
    return lastDot !== -1 ? filename.substring(lastDot).toLowerCase() : '';
  };

  const validateFile = useCallback(
    (file: File): { valid: boolean; error?: ValidationError } => {
      const extension = getFileExtension(file.name);

      // Check file format
      if (!acceptedFormats.includes(extension)) {
        return {
          valid: false,
          error: {
            type: 'format',
            message: 'File format is not supported',
            details: `Please upload PDF, TXT, MD, or DOCX files`,
          },
        };
      }

      // Check file size
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        return {
          valid: false,
          error: {
            type: 'size',
            message: `File exceeds the ${maxSizeMB}MB limit`,
          },
        };
      }

      return { valid: true };
    },
    [acceptedFormats, maxSizeMB]
  );

  const processFiles = useCallback(
    (files: FileList | File[]) => {
      if (disabled) return;

      // Clear previous errors and skipped files
      setError(null);
      setSkippedFiles([]);

      const fileArray = Array.from(files);

      // Check batch limit
      if (fileArray.length > maxFiles) {
        setError({
          type: 'batch',
          message: `You can upload up to ${maxFiles} files at a time`,
        });
        return;
      }

      const validFiles: File[] = [];
      const skipped: string[] = [];

      // Validate each file
      for (const file of fileArray) {
        const validation = validateFile(file);
        if (validation.valid) {
          validFiles.push(file);
        } else {
          skipped.push(file.name);
        }
      }

      // If all files are invalid (single file case or all same error)
      if (validFiles.length === 0 && skipped.length > 0) {
        // Validate the first file again to get the specific error
        const validation = validateFile(fileArray[0]);
        if (validation.error) {
          setError(validation.error);
        }
        return;
      }

      // If some files were skipped, show warning
      if (skipped.length > 0) {
        setSkippedFiles(skipped);
      }

      // Call the callback with valid files
      if (validFiles.length > 0) {
        onFilesSelected(validFiles);
      }
    },
    [disabled, maxFiles, validateFile, onFilesSelected]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
    },
    [disabled, processFiles]
  );

  const handleClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
        e.preventDefault();
        handleClick();
      }
    },
    [disabled, handleClick]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    },
    [processFiles]
  );

  return (
    <div className="w-full">
      <div
        data-testid="file-upload-zone"
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Upload files by dragging and dropping or clicking to select"
        aria-disabled={disabled}
        className={`
          relative flex flex-col items-center justify-center w-full p-8
          border-2 border-dashed rounded-lg cursor-pointer transition-all
          ${isDragOver ? 'drag-over border-blue-500 bg-blue-50' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'}
          ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-900' : 'hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <input
          ref={fileInputRef}
          data-testid="file-input"
          type="file"
          multiple
          accept={formatAcceptString}
          className="hidden"
          onChange={handleFileInputChange}
          disabled={disabled}
        />

        <div className="flex flex-col items-center space-y-4">
          {isDragOver ? (
            <FileText className="w-12 h-12 text-primary-500" />
          ) : (
            <Upload className="w-12 h-12 text-gray-400" />
          )}

          <div className="text-center">
            <p className="text-base text-gray-700 dark:text-gray-300">
              Drag and drop files here
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              or click to select files
            </p>
          </div>

          <div className="text-center text-sm text-gray-500 dark:text-gray-400">
            <p>Supported formats: PDF, TXT, MD, DOCX</p>
            <p>Maximum file size: {maxSizeMB} MB</p>
          </div>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-700 dark:text-red-400 font-medium">{error.message}</p>
            {error.details && (
              <p className="text-sm text-red-600 mt-1">{error.details}</p>
            )}
          </div>
        </div>
      )}

      {/* Skipped files warning */}
      {skippedFiles.length > 0 && (
        <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-yellow-500 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              {skippedFiles.map(name => `${name} was skipped`).join(', ')} - unsupported format
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadZone;
